/**
 * Handles app errors and shows them on notifications.
 * @ngesdoc service
 * @ngname appErrorHandler
 * @ngmodule common
 */
class AppErrorHandler {
  /**
   * @param {ngLog}      $log       To log the errors on the console.
   * @param {UIMessages} uimessages To show error notifications.
   */
  constructor($log, uimessages) {
    'ngInject';

    /**
     * A local reference for the `$log` service.
     * @type {ngLog}
     */
    this.$log = $log;
    /**
     * A local reference for the `uimessages` service.
     * @type {UIMessages}
     */
    this.uimessages = uimessages;
  }
  /**
   * Handle an error.
   * @param {Error|Object} error              The error can ba en `Error` object or just an object
   *                                          with a `message` property. If it's not an `Error`
   *                                          object, it won't be logged on the console.
   * @param {string}       [customMessage=''] A custom message for the notification. If not
   *                                          specified, the method will try to use the `message`
   *                                          property of the received `error`.
   * @param {boolean}      [silent=false]     If `true`, the notification won't be shown, just the
   *                                          logging on the console. Be aware the if `error` is
   *                                          not an `Error` object and this is `true`, nothing
   *                                          will happen.
   */
  handle(error, customMessage = '', silent = false) {
    // If the notification needs to be shown...
    if (!silent) {
      // ...get the message to show.
      const message = customMessage || error.message || 'Unexpected error';
      // Push the notification to the UI.
      this.uimessages.notification(
        `${message}. Please try again.`,
        {
          type: 'error',
          expiration: 10000,
        },
      );
    }
    // If the error is an instance of `Error`, log it on the console.
    if (error instanceof Error) {
      this.$log.error(error);
    }
  }
  /**
   * A short hand method to log an error message on the console.
   * @param {Error} error The error to log.
   */
  silent(error) {
    this.handle(error, '', true);
  }
}

export default AppErrorHandler;
